import { useState, useEffect } from "react";
import {
    listExports,
    createExcelExport,
    deleteFile as deleteFileExternal
} from "../../../../apis/apiExports";

const useExportablesFiles = () => {
    // States for query parameters 
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(5);
    const [lastDays, setLastDays] = useState(60);
    const [selectedFile, setSelectedFile] = useState({});
    const [selectedSection, setSelectedSection] = useState('all');
    // List files and metadata
    const [exportablesFiles, setExportablesFiles] = useState([]);
    const [meta, setMeta] = useState(null);
    const [lastExportablesFiles, setLastExportablesFiles] = useState([]);
    // Alias lists for compare last files and files
    const [lastAlias, setLastAlias] = useState([]);
    // General info
    const [areThereFiles, setAreThereFiles] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState(
        {
            exportablesFilesError: null,
            lastExportablesFilesError: null,
            createFileError: null,
            deleteFileError: null,
        }
    );    

    const getListExports = async (propsForUpdate) => {    // propsForUpdate = { selectedSection, page, pageSize, lastDays }
        const { data, meta } = await listExports(propsForUpdate);

        if (data !== undefined && data.length >= 0) {
            return [meta, data];
        }
        
        return [[], null];
    };

    const updateFiles = async (parameters={ selectedSection, page, pageSize, lastDays }) => {
        setIsLoading(true);
        try {
            const [metaData, filesData] = await getListExports(parameters);
            setMeta(metaData);
            setExportablesFiles(filesData);
        } catch (err) {
            setMeta(null);
            setExportablesFiles([]);
            setErrors({...errors, exportablesFilesError: err});
        } finally {
            setIsLoading(false);
        }
    };

    const updateLastFiles = async () => {
        const [_, filesData] = await getListExports({ selectedSection: 'all', page: 1, pageSize: 3, lastDays: 60 });
    
        try {
            if (filesData) {
                setLastExportablesFiles([...filesData]);
                setLastAlias(filesData.map((element) => element.alias));
            } else {
                setLastExportablesFiles([]);
                setLastAlias([]);
            }
        } catch (err) {
            setErrors((prevErrors) => ({ ...prevErrors, lastExportablesFilesError: err }));
        } finally {
            setIsLoading(false);
        }
    };
    

    const createFunction = async (data, languaje, kindFile) => {
        const response = await createExcelExport(data, languaje, kindFile);
        return response;
    };

    const createFile = (data, languaje, kindFile) => {
        setIsLoading(true);

        try {
            createFunction(data, languaje, kindFile);
            updateLastFiles();
            updateFiles();
        } catch (err) {
            setErrors({...errors, createFileError: err});
        } finally {
            setIsLoading(false);
        }
    };

    const deleteFunction = async (alias) => {
        const response = await deleteFileExternal(alias);
        return response;
    };

    const deleteFile = async (alias) => {
        setIsLoading(true);
    
        try {
            await deleteFunction(alias);
            await updateLastFiles();
            await updateFiles();
        } catch (err) {
            setErrors((prevErrors) => ({ ...prevErrors, deleteFileError: err }));
        } finally {
            setIsLoading(false);
        }
    };
    
    const updateAll = () => {
        updateFiles();
        updateLastFiles();
    };

    const updateFromComponent = (parameters) => {
        updateFiles(
            {
                selectedSection: parameters.selectedSection ? parameters.selectedSection : selectedSection,
                page: parameters.page ? parameters.page : page,
                pageSize: parameters.pageSize ? parameters.pageSize: pageSize,
                lastDays: parameters.lastDays ? parameters.lastDays: lastDays,
            }
        );
    };

    useEffect(() => {
        const fetchData = async () => {
            await updateFiles();
            await updateLastFiles();
        };
        fetchData();
    }, []);

    useEffect(() => {
        const isExportablesFilesValid = Array.isArray(exportablesFiles) && exportablesFiles.length > 0;
        const isLastExportablesFilesValid = Array.isArray(lastExportablesFiles) && lastExportablesFiles.length > 0;
    
        if (isExportablesFilesValid || isLastExportablesFilesValid) {
            return setAreThereFiles(true);
        } else {
            
            return setAreThereFiles(false);
        }
    }, [exportablesFiles, lastExportablesFiles]);
    
    return {
        // States
        pageState: [page, setPage],
        pageSizeState: [pageSize, setPageSize],
        lastDaysState: [lastDays, setLastDays],
        selectedFileState: [selectedFile, setSelectedFile],
        selectedSectionState: [selectedSection, setSelectedSection],
        // data and functions
        exportablesFiles,
        lastExportablesFiles,
        meta,
        createFile,
        updateFiles,
        updateLastFiles,
        updateAll,
        deleteFile,
        updateFromComponent,
        areThereFiles,
        isLoading,
        errors,
    };
};

export {
    useExportablesFiles,
}
